import { updateStateData } from "../Store/actions";
import store from "../Store/store";
import { notifyApiSuccess } from "./apiEvents";
import { deletePaginationCookies } from "./utils";
import { getBackendUrl } from "./runtimeEnv";
import {
  RETAILER_SELECTION_COOKIE_KEYS,
  RETAILER_SELECTION_OPTIONS_KEY,
} from "./retailerSelectionConfig";
import { clearRetailerScopeNoticeDismissal } from "../components/commonComponents/Hoc/accessControlToasts";
import { COOKIE_MAX_AGE_MS, clearSessionEndsAt } from "./authTokenUtils";

// Login / post-auth orchestration lives in loginFlow.js (re-exported at bottom for existing imports).

let controller = new AbortController();

export function deleteCookie(cookieName, includePath = true) {
  if (typeof includePath !== "undefined" && includePath === false) {
    // Set the cookie with the same name and path to delete it
    document.cookie = cookieName + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
  } else {
    // Set the cookie with the same name and path to delete it
    document.cookie = cookieName + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  }

}

export function createCookie(cookieName, cookieValue) {
  let date = new Date();
  date.setTime(date.getTime() + COOKIE_MAX_AGE_MS);
  document.cookie = cookieName + "=" + cookieValue + "; expires = " + date.toGMTString() + "; path=/; Secure; SameSite=Strict";
}

export const deleteAnnouncementSeenCookies = () => {
  if (typeof document === "undefined" || typeof document.cookie !== "string") {
    return;
  }
  document.cookie.split(";").forEach((entry) => {
    const name = entry.split("=")[0].trim();
    if (name.indexOf("announcementSeen_") === 0) {
      deleteCookie(name);
      document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=Strict";
    }
  });
};

// In memory only (no cookie / browser storage): set on login and consumed by the announcement inbox
// on its first mount. Login navigates client-side, while F5, workspace switch and logout reload the
// page and reset this module, so the side panel auto-opens once per login only.
let announcementAutoOpenPending = false;

export const consumeAnnouncementAutoOpenPending = () => {
  const pending = announcementAutoOpenPending;
  announcementAutoOpenPending = false;
  return pending;
};

// Login lives in loginFlow.js. That flow sets this so the inbox opens once after a fresh login.
export const markAnnouncementAutoOpenPending = () => {
  announcementAutoOpenPending = true;
};

//fetch auth token from cookies
export const fetchToken = () => {
  if (typeof document.cookie.split("; ").find((row) => row.startsWith("authToken")) !== "undefined") {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith("authToken"))
      .split("=")[1];
  }
  return "";
};

export const fetchBrowserNotificationCookie = () => {
  if (typeof document.cookie.split("; ").find((row) => row.startsWith("browserNotification")) !== "undefined") {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith("browserNotification"))
      .split("=")[1];
  }
  return "";
};

/** Clear retailer-selection cookies + Redux (shared by logout and tenant switch). */
export const clearRetailerSelectionData = () => {
  const emptyRetailerSelectionState = RETAILER_SELECTION_COOKIE_KEYS.reduce((selectionData, cookieKey) => {
    return {
      ...selectionData,
      [cookieKey]: cookieKey === RETAILER_SELECTION_OPTIONS_KEY ? [] : {},
    };
  }, {});

  RETAILER_SELECTION_COOKIE_KEYS.forEach((cookieName) => {
    deleteCookie(cookieName);
  });
  clearRetailerScopeNoticeDismissal();
  store.dispatch(updateStateData(emptyRetailerSelectionState));
};

// to logout
export const logout = () => {
  clearSessionEndsAt();
  deleteCookie("authToken");
  deleteCookie("userPermissions");
  deleteCookie("workspace_options");
  deleteCookie("current_workspace");
  deleteCookie("tenant_options");
  deleteCookie("selected_tenant");
  deleteCookie("userRole");
  deleteCookie("userDetail");
  deleteCookie("browserNotification");
  deleteCookie("access_types");
  clearRetailerSelectionData();
  deleteAnnouncementSeenCookies();
  deletePaginationCookies();
  window.location.href = "/";
};

export const setLoadingStatus = (status) => {
  //update redux state to start a loader whenever a new request is send from the application
  store.dispatch(updateStateData({ loading: status }));
};

export const setBatchLoadingStatus = (status) => {
  //update redux state to start a loader whenever a new request is send from the application
  //console.trace("Batchload triggered to: " + status )
  store.dispatch(updateStateData({ batchLoading: status }));
};

export const triggerAppContextRefresh = (source, details = {}) => {
  const currentStateData = store.getState().userSelections;
  const currentRefreshVersion =
    typeof currentStateData.app_context_refresh_version === "number"
      ? currentStateData.app_context_refresh_version
      : 0;
  const nextRefreshVersion = currentRefreshVersion + 1;

  store.dispatch(
    updateStateData({
      app_context_refresh_version: nextRefreshVersion,
      app_context_last_change: {
        source,
        changed_at: Date.now(),
        ...details,
      },
    })
  );

  return nextRefreshVersion;
};

// Holds the full-screen Backdrop steady across a burst of requests WITHOUT touching
// batchLoading. The listing data tables bind their own "loading" prop to batchLoading
// and would blank their rows to a "Loading…" placeholder if it toggled - this flag is
// only read by the global Loader, so modal-scoped batches don't disturb background lists.
export const setOverlayLoadingStatus = (status) => {
  store.dispatch(updateStateData({ overlayLoading: status }));
};

export const setFiltersBatchLoadingStatus = (status) => {
  //update redux state to start a loader whenever a new request is send from the application
  //console.trace("Batchload triggered to: " + status )
  store.dispatch(updateStateData({ filtersBatchLoading: status }));
};

export const sendApiRequestWithFile = async (apiPath, requestBody, requestMethod) => {
  // re-initialize the controller if the previous request was aborted
  if (controller.signal.aborted) {
    controller = new AbortController();
  }
  const authToken = fetchToken();
  const requestHeaders = new Headers();
  //auth token must be added to headers object (If available)
  if (authToken !== "") {
    requestHeaders.append("Authorization", "Bearer " + authToken);
  }
  setLoadingStatus(true);
  //send request
  const checkResponse = await fetch(getBackendUrl() + apiPath, {
    method: requestMethod,
    mode: "cors",
    headers: requestHeaders,
    body: requestMethod === "GET" ? null : requestBody,
    signal: controller.signal,
  });

  if (checkResponse.status !== 200) {
    setLoadingStatus(false);
    //redirect back to login screen when status is 401 (unauthorised) or 500 (internal server error)
    if (checkResponse.status === 401 || checkResponse.status === 500) {
      // window.location.href = "/";
    }
    return {
      success: false,
      message: "Sorry, something went wrong",
    };
  } else {
    setLoadingStatus(false);
    let response = {};
    if (requestMethod !== "DELETE") {
      response = await checkResponse.json();
    }
    return {
      success: true,
      response: response,
    };
  }
};

export const cancelOngoingRequests = () => {
  controller.abort();
  setLoadingStatus(false);
  setBatchLoadingStatus(false);
  setOverlayLoadingStatus(false);
  store.dispatch(updateStateData({
    show_loading_cancel_button: false,
  }));
};

const isRequestOptions = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

// retailerSelection imports this file, so the lookup is loaded when the request runs.
const retailerSelectionApi = () => require("./retailerSelection");

// Reads keep a caller query string and add filter_view_company_id after it.
// companies/share-list?type=stores becomes ...?type=stores&filter_view_company_id=id.
// A path with no ? gets ?filter_view_company_id=id. Writes skip this.
// Listing filter dropdowns (including Owner) receive the param. Create owner trees and the bottom-nav retailer list still pass skipRetailerViewFilter.
export const appendFilterViewCompanyId = (apiPath, requestMethod, skipRetailerViewFilter = false) => {
  if (requestMethod !== "GET" || skipRetailerViewFilter === true) {
    return apiPath;
  }
  if (typeof apiPath !== "string" || apiPath.includes("filter_view_company_id=")) {
    return apiPath;
  }

  const companyId = retailerSelectionApi().resolveFilterViewCompanyId();
  if (companyId === "" || companyId === null || typeof companyId === "undefined") {
    return apiPath;
  }

  const separator = apiPath.includes("?") ? "&" : "?";
  return apiPath + separator + "filter_view_company_id=" + encodeURIComponent(companyId);
};

// Empty 204 bodies are success for background calls. Writes may answer with
// plain text. A GET that cannot be parsed is still an error.
const parseResponseBody = async (checkResponse, requestMethod) => {
  const rawText = await checkResponse.text();
  if (!rawText) {
    return null;
  }

  try {
    return JSON.parse(rawText);
  } catch (error) {
    if (requestMethod === "GET") {
      throw error;
    }
    return rawText;
  }
};

const abortedResult = () => ({
  success: false,
  aborted: true,
});

/* common function to send api request and returning the response back to the calling function.

   4th argument is either the existing loaderControl boolean, or an options object:
     { silent, signal, loaderControl, skipRetailerViewFilter }
   Existing callers (3 args, or 4th = true/false) are unchanged.
   skipRetailerViewFilter: this GET stays unscoped (create owner hierarchy, bottom-nav retailer roots, and similar).
   Listing filter dropdowns omit this flag so they receive filter_view_company_id. Non-GET calls never receive the param.

   silent: background request. No loader, no api-success event, 204/empty bodies
   are allowed, network errors return { success: false } instead of rejecting.
   signal: abort this request only. A silent call without a signal is not tied
   to the shared controller, so cancelOngoingRequests() cannot kill polling. */
export const sendApiRequest = async (apiPath, requestBody, requestMethod, loaderControl = false) => {
  const options = isRequestOptions(loaderControl) ? loaderControl : {};
  const keepLoaderOn = isRequestOptions(loaderControl)
    ? options.loaderControl === true
    : loaderControl !== false;
  const silent = options.silent === true;
  const requestSignal = options.signal;
  // One path for the first try and the retry. Query params already on apiPath stay put.
  const requestPath = appendFilterViewCompanyId(
    apiPath,
    requestMethod,
    options.skipRetailerViewFilter === true
  );
  const usesSharedController = silent === false && typeof requestSignal === "undefined";

  // re-initialize the controller if the previous request was aborted
  if (usesSharedController && controller.signal.aborted) {
    controller = new AbortController();
  }

  const updateLoader = (status) => {
    if (silent === false) {
      setLoadingStatus(status);
    }
  };

  const isAborted = () =>
    (requestSignal && requestSignal.aborted === true) ||
    (usesSharedController && controller.signal.aborted === true);

  let authToken = fetchToken();
  let requestHeaders = new Headers();
  requestHeaders.append("Content-Type", "application/json");

  if (authToken !== "") {
    requestHeaders.append("Authorization", "Bearer " + authToken);
  }

  let retry = false;
  let retryCount = 0;
  // if api call fails in first attempt, system will retry the api one more time.
  try {
    do {
      if (silent === true && isAborted()) {
        return abortedResult();
      }

      updateLoader(true);
      // api request
      const checkResponse = await fetch(getBackendUrl() + requestPath, {
        method: requestMethod,
        mode: "cors",
        headers: requestHeaders,
        body: requestMethod === "GET" ? null : JSON.stringify(requestBody),
        signal: usesSharedController ? controller.signal : requestSignal,
      });
      // response check
      if (checkResponse.status === 401 || checkResponse.status === 500) {
        retry = true;
        retryCount++;

        if (retryCount > 1) {
          // If retry fails, redirect to login page
          updateLoader(false);
          // window.location.href = "/";
          return {
            success: false,
            message: "Unauthorized. Redirecting to login page...",
          };
        } else {
          // Retry after fetching a new token
          authToken = fetchToken();
          requestHeaders.set("Authorization", "Bearer " + authToken);
        }
      } else if (
        checkResponse.status !== 200 &&
        checkResponse.status !== 201 &&
        !(silent === true && checkResponse.status === 204)
      ) {
        updateLoader(false);
        // Handle other error statuses
        return {
          success: false,
          message: "Sorry, something went wrong",
        };
      } else {
        // Reset retry on successful response
        retry = false;

        if (keepLoaderOn === false) {
          updateLoader(false);
        }
        let response = {};
        if (requestMethod !== "DELETE") {
          response =
            silent === true ? await parseResponseBody(checkResponse, requestMethod) : await checkResponse.json();
        }

        const success =
          silent === true
            ? !(response && typeof response === "object" && !Array.isArray(response) && response.error === true)
            : typeof response.error !== "undefined" && response.error === true
              ? false
              : true;
        if (success && silent === false) {
          notifyApiSuccess(apiPath);
        }
        return {
          success,
          response: response,
        };
      }
    } while (retry);
  } catch (error) {
    if (silent === false) {
      throw error;
    }

    if ((error && error.name === "AbortError") || isAborted()) {
      return abortedResult();
    }

    return {
      success: false,
      message: "Sorry, something went wrong",
    };
  }
};

export const checkAccessType = async () => {
  const accessTypes = await sendApiRequest("users/access", {}, "GET");
  //check if data variable has token or not
  if (typeof accessTypes.success !== "undefined" && accessTypes.success === true) {
    const accessItems = accessTypes.response;
    const hasMultipleAccessTypes = accessItems.length > 1 ? true : false;
    const accessRespObj = {
      has_multiple_access: hasMultipleAccessTypes,
      access_types: accessItems,
    }
    // show popup
    store.dispatch(
      updateStateData(accessRespObj)
    );
    createCookie("access_types", JSON.stringify(accessRespObj));
  } else {
    return {
      success: false,
    };
  }
}

// to generate token for embeddable authorization.

export const generateEmbeddableToken = async (requestBody) => {
  setLoadingStatus(true);
  const embeddableEnv = findEmbeddableEnvironmentInfo();
  let environment = embeddableEnv.environment;
  let savedVersion = embeddableEnv.savedVersion;

  // Base auth payload used by all Embeddable dashboards
  const authPayload = {
    embeddable_id: requestBody.embeddableId,
    environment: environment,
    saved_version: savedVersion,
    number_root: requestBody.securityContext.number_root,
  };

  // Campaign analytics only: include campaign_id when the caller provides it
  if (
    typeof requestBody.campaignId !== "undefined" &&
    requestBody.campaignId !== null &&
    requestBody.campaignId !== ""
  ) {
    authPayload.campaign_id = requestBody.campaignId;
  }

  // This call is a POST, so the shared GET append does not run.
  // Pass GET here only to attach filter_view_company_id. The request itself stays a POST.
  const authenticatePath = appendFilterViewCompanyId("api/embeddable/authenticate", "GET");
  const tokenResponse = await sendApiRequest(
    authenticatePath,
    authPayload,
    "POST"
  );

  const response = tokenResponse.response;
  return {
    success: true,
    response: response.token,
  };
};

export const findEmbeddableEnvironmentInfo = () => {
  let environment = process.env.REACT_APP_ENV_NAME === "prod" ? "prod" : process.env.REACT_APP_ENV_NAME === "demo" ? "demo" : "staging";
  let savedVersion = process.env.REACT_APP_ENV_NAME === "prod" ? "production" : process.env.REACT_APP_ENV_NAME === "demo" ? "development" : "staging";
  return {
    environment: environment,
    savedVersion: savedVersion,
  }
};

// ---------------------------------------------------------------------------
// Login / post-auth orchestration — implemented in loginFlow.js
// Re-export so existing imports from requestConfig keep working unchanged.
// ---------------------------------------------------------------------------
export {
  authorizeCredentials,
  checkAuthorizedUserDetails,
  checkSelectedTenantFromCookies,
  checkTenantOptionsFromCookies,
  checkTenants,
  fetchWorkSpaceList,
  findUsersAccesstype,
  refreshAuthorizedUserDetails,
  selectTenantAccess,
} from "./loginFlow";

// Live maintenance flag. The API spells the field "maintanence_mode".
// sendApiRequest turns on the app loader. A failed request leaves the site open.
export const fetchExplorerSettings = async () => {
  try {
    const settings = await sendApiRequest("explorer-settings", {}, "GET", {
      skipRetailerViewFilter: true,
    });

    if (settings?.success !== true) {
      return { maintanence_mode: false };
    }

    return {
      maintanence_mode: settings.response?.maintanence_mode === true,
    };
  } catch {
    setLoadingStatus(false);
    return { maintanence_mode: false };
  }
};

export const checkBackendHealth = async (navigate) => {
  try {
    let requestHeaders = new Headers();
    requestHeaders.append("Content-Type", "application/json");

    const response = await fetch(
      getBackendUrl() + "actuator/health",
      {
        method: "GET",
        mode: "cors",
        headers: requestHeaders,
      }
    );

    if (response.ok) {
      setLoadingStatus(false);

      return {
        success: true,
        message: "Backend is healthy",
      }
    }
  } catch (error) {
    setLoadingStatus(false);
    // This usually means:
    // - CORS error
    // - Network issue
    // - DNS failure
    // - Backend unreachable

    if (error instanceof TypeError) {
      if (window.location.pathname !== "/" && window.location.pathname !== "/login") {
        navigate("/");
      }
      return {
        success: false,
        message: "Unable to connect to server. Possible CORS or network issue.",
      }
    } else {
      if (window.location.pathname !== "/" && window.location.pathname !== "/login") {
        navigate("/");
      }
      return {
        success: false,
        message: "Unexpected error occurred.",
      }
    }
  }
};
